import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosinstance";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { Snackbar, Alert as MuiAlert } from "@mui/material"; // Import MuiAlert as Alert
import "./Forms.css";
import Select from "react-select";
import { FiEye, FiEyeOff } from "react-icons/fi";

// Yup validation schema
const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
  phone_number: Yup.string()
    .matches(/^\d{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
  role: Yup.string().required("Role is required"),
});

export default function UserForm({ isSidebarExpanded }) {
  // Initialize hooks
  const navigate = useNavigate(); // Hook for navigation

  // State for controlling Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [roles, setRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      confirm_password: "",
      phone_number: "",
      role: "",
    
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        // Check if email already exists
        try {
          const checkResponse = await axiosInstance.get(
            `/api/check_email/${values.email}`
          );

          if (checkResponse.data.exists) {
            setSnackbarSeverity("error");
            setSnackbarMessage("Email already exists!");
            setSnackbarOpen(true);
            return;
          }
        } catch (error) {
          console.error("Error checking email:", error);
        }

        const response = await axiosInstance.post(`/api/register_user`, values);

        if (response.status === 200) {
          setSnackbarSeverity("success");
          setSnackbarMessage("User added successfully!");
          setSnackbarOpen(true);

          // Reset form after successful submission
          resetForm();

          // Navigate after delay
          setTimeout(() => {
            navigate("/system-users");
          }, 2000);
        }
      } catch (error) {
        console.error("Error submitting the form:", error);
        setSnackbarSeverity("error");
        setSnackbarMessage(
          error.response?.data?.message ||
            "Error submitting the form. Please try again."
        );
        setSnackbarOpen(true);
      }
    },
  });

  useEffect(() => {
    //console.log("useEffect triggered");
    const fetchRoles = async () => {
      try {
        const response = await axiosInstance.get(`/api/get_all_roles`);

        console.log(response.data);

        setRoles(
          response.data.data.map((role) => ({
            value: role.role_id, // Assuming API returns id
            label: role.role_name, // Assuming API returns name
          }))
        );
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    fetchRoles();
  }, []);


  // Handle save button click to trigger validation
  const handleSaveClick = (e) => {
    e.preventDefault();

    // Mark all fields as touched to show validation errors
    formik.setTouched({
      name: true,
      email: true,
      password: true,
      confirm_password: true,
      phone_number: true,
      role: true,
    });

    // Trigger validation on all fields
    formik.validateForm().then(() => {
      // If validation is successful, submit the form
      if (!Object.keys(formik.errors).length) {
        formik.handleSubmit();
      }
    });
  };

  // Close snackbar after it's shown
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handlecancel = () => {
    window.history.back();
  };

  const handleChange = (selectedOption) => {
    formik.setFieldValue("role", selectedOption ? selectedOption.value : "");
  };

  // Handle blur for react-select
  const handleBlur = () => {
    formik.setFieldTouched("role", true);
  };

  const options = roles.map((role) => ({
    value: role.value,
    label: role.label,
  }));

  return (
    <div
      className={`table-container user-form-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body user-form-content">
        <div className="user-form-page-heading">
          <h1 className="form-title jost-bold">New User</h1>
          <p className="user-form-page-subtitle">
            Create a system user and assign the appropriate access role.
          </p>
        </div>
        <div className="user-form-card">
          <div className="form-body-user user-form-body">
          <form className="form-box user-form-box">
            <div className="form-body-container user-form-sections">
              <section className="user-form-section user-form-section-last">
                <div className="user-form-section-header">
                  <div>
                    <h2 className="user-form-section-title">User Details</h2>
                    <p className="user-form-section-description">
                      Add contact, role and password information for the new
                      system user.
                    </p>
                  </div>
                </div>
            <div className="form-group">
              <label className="form-label">
                Name <span className="required">*</span>
              </label>
              <div className="form-inputs">
                <input
                  type="text"
                  className="input-field"
                  name="name"
                  placeholder="Enter User's Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                  autoFocus
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="error-message">{formik.errors.name}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Email<span className="required">*</span>
              </label>
              <div className="form-inputs">
                <input
                  type="email"
                  className="input-field"
                  name="email"
                  placeholder="Enter Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="error-message">{formik.errors.email}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Phone Number <span className="required">*</span>
              </label>
              <div className="form-inputs">
                <input
                  type="number"
                  className="input-field"
                  name="phone_number"
                  placeholder="Enter Phone Number"
                  value={formik.values.phone_number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.phone_number && formik.errors.phone_number && (
                  <div className="error-message">
                    {formik.errors.phone_number}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Role<span className="required">*</span>
              </label>
              <div className="form-inputs">
                <Select
                  className="input-field-role"
                  name="role"
                  value={roles.find(
                    (option) => option.value === formik.values.role
                  )}
                  onChange={(selectedOption) => {
                    formik.setFieldValue(
                      "role",
                      selectedOption ? selectedOption.value : ""
                    );
                  }}
                  onBlur={() => formik.setFieldTouched("role", true)}
                  options={roles}
                  placeholder="Select User Role"
                  isClearable
                  required
                />

                {formik.touched.role && formik.errors.role && (
                  <div className="error-message">{formik.errors.role}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Password<span className="required">*</span>
              </label>
              <div className="form-inputs">
                <div className="password-field-shell">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="input-field password-field-input"
                    name="password"
                    placeholder="Enter Password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  <button
                    type="button"
                    className="password-visibility-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <div className="error-message">{formik.errors.password}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Confirm Password<span className="required">*</span>
              </label>
              <div className="form-inputs">
                <div className="password-field-shell">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="input-field password-field-input"
                    name="confirm_password"
                    placeholder="Confirm your Password"
                    value={formik.values.confirm_password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  <button
                    type="button"
                    className="password-visibility-toggle"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={
                      showConfirmPassword ? "Hide confirm password" : "Show confirm password"
                    }
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {formik.touched.confirm_password &&
                  formik.errors.confirm_password && (
                    <div className="error-message">
                      {formik.errors.confirm_password}
                    </div>
                  )}
              </div>
            </div>
            </section>
            </div>
          </form>
        </div>
        <div className="form-footer">
        <div className="button-group">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handlecancel}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="btn btn-save"
            onClick={handleSaveClick} // Call handleSaveClick to trigger validation
          >
            SAVE
          </button>
        </div>
        </div>
        </div>
      </div>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(50px)", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity="success">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
